import { useEffect, useMemo, useRef, useState } from "react";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";

import { notify } from "../../../components/Notification";
import { DeletePopUp } from "../../../components/PopUp";
import { ErrorState } from "../../../components/States";
import { Skeleton } from "../../../components/Skeleton";
import { DateFormat, formatCurrency, formatDate } from "../../../utils";
import { AccountType } from "../../Accounts/types";
import {
  CreateSavingsGoalPayload,
  SavingsGoalResponse,
  useCreateSavingsGoalMutation,
  useDeleteSavingsGoalMutation,
  useGetAccountsQuery,
  useGetSavingsGoalsQuery,
  useUpdateSavingsGoalMutation,
} from "../../../services";
import { SavingsGoalPanel } from "../../Savings/panels";
import { PastGoals } from "./PastGoals";
import { PastSavingsGoalSeries } from "./PastSavingsGoalSeries";
import { SavingsGoalCard } from "./SavingsGoalCard";
import { buildSavingsGoalSeries, newestOf } from "./savingsGoalSeries";
import {
  SavingsGoalPanelMode,
  SavingsGoalsProps,
  SavingsGoalSeries,
} from "./types";
import {
  Card,
  CardsGrid,
  EmptyIcon,
  EmptyState,
  EmptyText,
  Header,
  Group,
  Hint,
  PastList,
  Title,
} from "./styles";
import Button, { ButtonTypes } from "../../../components/Button";

// Two placeholder cards while the first fetch is in flight — enough to signal
// "content is loading here" without guessing the real count.
const SKELETON_KEYS = [0, 1];

export const SavingsGoals = ({
  showPast = false,
  rollForwardGoalId,
}: SavingsGoalsProps) => {
  const [panelMode, setPanelMode] = useState<SavingsGoalPanelMode | null>(null);
  const [goalToDelete, setGoalToDelete] = useState<SavingsGoalResponse | null>(
    null,
  );

  const goalsQuery = useGetSavingsGoalsQuery();
  const accountsQuery = useGetAccountsQuery();
  const [createGoal] = useCreateSavingsGoalMutation();
  const [updateGoal] = useUpdateSavingsGoalMutation();
  const [deleteGoal, { isLoading: isDeleting }] =
    useDeleteSavingsGoalMutation();

  const allGoals = useMemo(() => goalsQuery.data ?? [], [goalsQuery.data]);
  const activeGoals = useMemo(
    () => allGoals.filter((goal) => goal.closedAt === null),
    [allGoals],
  );
  const { series, predecessorOf, hasSuccessor } = useMemo(
    () => buildSavingsGoalSeries(allGoals),
    [allGoals],
  );
  const pastGoalCount = series.reduce(
    (total, entry) => total + entry.goals.length,
    0,
  );
  const hasGoals = allGoals.length > 0;

  // First load owns the skeleton; a failed first load owns the error state.
  // Background refetches keep the existing cards mounted so the section never
  // flashes empty (the toast still surfaces the underlying API error).
  const showError = goalsQuery.isError && !hasGoals;
  const showSkeleton = goalsQuery.isLoading && !hasGoals && !showError;
  const showEmpty = !goalsQuery.isLoading && !hasGoals && !showError;

  const savingsAccounts = useMemo(
    () =>
      (accountsQuery.data ?? [])
        .filter(
          (account) =>
            account.accountType === AccountType.SAVINGS && account.isActive,
        )
        .map((account) => ({ id: account.id, name: account.name })),
    [accountsQuery.data],
  );

  const editingGoal = panelMode?.kind === "edit" ? panelMode.goal : undefined;
  const rollForwardSource =
    panelMode?.kind === "rollForward" ? panelMode.goal : undefined;

  const openCreate = () => setPanelMode({ kind: "create" });

  const openEdit = (goal: SavingsGoalResponse) =>
    setPanelMode({ kind: "edit", goal });

  const openRollForward = (goal: SavingsGoalResponse) =>
    setPanelMode({ kind: "rollForward", goal });

  const closePanel = () => setPanelMode(null);

  const handleSubmit = async (
    payload: CreateSavingsGoalPayload,
    id?: number,
  ) => {
    try {
      if (id === undefined) {
        await createGoal(payload).unwrap();
        notify.success(
          payload.previousGoalId === undefined
            ? "Savings goal created!"
            : "Savings goal rolled forward!",
        );
      } else {
        await updateGoal({ id, payload }).unwrap();
        notify.success("Savings goal updated!");
      }
      closePanel();
    } catch {
      // errorToastMiddleware surfaces the API error; keep the panel open.
    }
  };

  const confirmDelete = async () => {
    if (!goalToDelete) {
      return;
    }
    try {
      await deleteGoal(goalToDelete.id).unwrap();
      notify.success("Savings goal deleted!");
      setGoalToDelete(null);
    } catch {
      // errorToastMiddleware surfaces the API error; keep the modal open.
    }
  };

  const skeleton = (
    <CardsGrid>
      {SKELETON_KEYS.map((key) => (
        <Card key={key} aria-hidden="true">
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="text" width="65%" height="28px" />
          <Skeleton variant="rectangular" width="100%" height="8px" />
          <Skeleton variant="text" width="55%" />
        </Card>
      ))}
    </CardsGrid>
  );

  const cards = (
    <CardsGrid>
      {activeGoals.map((goal) => (
        <SavingsGoalCard
          key={goal.id}
          goal={goal}
          predecessor={predecessorOf(goal.id)}
          onEdit={openEdit}
          onDelete={setGoalToDelete}
        />
      ))}
    </CardsGrid>
  );

  const emptyState = (
    <EmptyState>
      <EmptyIcon>
        <TrackChangesRoundedIcon fontSize="large" />
      </EmptyIcon>
      <EmptyText>
        No savings goals yet. Set a target and watch your progress toward it.
      </EmptyText>
      <Button
        type={ButtonTypes.primary}
        size="small"
        onClick={openCreate}
        data-testid="add-savings-goal"
      >
        + Add your first goal
      </Button>
    </EmptyState>
  );

  const renderBody = () => {
    if (showError) {
      return (
        <ErrorState
          message="We couldn't load your savings goals."
          onRetry={async () => {
            await goalsQuery.refetch();
          }}
        />
      );
    }
    if (showSkeleton) {
      return skeleton;
    }
    if (showEmpty) {
      return emptyState;
    }
    if (activeGoals.length === 0) {
      return <Hint>No active savings goals.</Hint>;
    }
    return cards;
  };

  const canRollForward = (entry: SavingsGoalSeries) =>
    !hasSuccessor(newestOf(entry).id);
  // The panel captures its prefilled accounts once, so rolling before they load would drop every link.
  const isRollForwardDisabled = accountsQuery.data === undefined;

  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (
      hasAutoOpened.current ||
      rollForwardGoalId === undefined ||
      isRollForwardDisabled
    ) {
      return;
    }
    const goal = allGoals.find(({ id }) => id === rollForwardGoalId);
    if (goal && goal.closedAt !== null && !hasSuccessor(goal.id)) {
      hasAutoOpened.current = true;
      setPanelMode({ kind: "rollForward", goal });
    }
  }, [rollForwardGoalId, isRollForwardDisabled, allGoals, hasSuccessor]);

  const isPastLinked = showPast || rollForwardGoalId !== undefined;
  const pastGoals = (
    <PastGoals
      count={pastGoalCount}
      isInitiallyOpen={isPastLinked}
      isScrollTarget={isPastLinked}
    >
      <PastList>
        {series.map((entry) => (
          <PastSavingsGoalSeries
            key={entry.id}
            series={entry}
            canRollForward={canRollForward(entry)}
            isRollForwardDisabled={isRollForwardDisabled}
            onRollForward={openRollForward}
            onDelete={setGoalToDelete}
          />
        ))}
      </PastList>
    </PastGoals>
  );

  return (
    <Group>
      <Header>
        <Title>Savings goals</Title>
        {!showEmpty && !showError && (
          <Button
            type={ButtonTypes.primary}
            size="small"
            onClick={openCreate}
            data-testid="add-savings-goal"
          >
            + Add goal
          </Button>
        )}
      </Header>

      {renderBody()}

      {pastGoals}

      {panelMode && (
        <SavingsGoalPanel
          goal={editingGoal}
          rollForwardFrom={rollForwardSource}
          accounts={savingsAccounts}
          isOpen
          onClose={closePanel}
          onSubmit={handleSubmit}
        />
      )}

      <DeletePopUp
        isOpen={goalToDelete !== null}
        setIsOpen={() => setGoalToDelete(null)}
        title="Delete savings goal"
        text={
          <>
            Do you really want to delete the{" "}
            <strong>{goalToDelete?.name}</strong> savings goal? This only
            removes the goal — your accounts and their balances are not
            affected.
          </>
        }
        details={
          goalToDelete
            ? {
                pill: { label: "SAVINGS GOAL", variant: "DEFAULT" },
                title: goalToDelete.name,
                subtitle: `${formatCurrency(
                  goalToDelete.targetAmount,
                )} · by ${formatDate(goalToDelete.targetDate, DateFormat.DMY_SPACE)}`,
              }
            : undefined
        }
        handleOnDelete={confirmDelete}
        isLoading={isDeleting}
      />
    </Group>
  );
};
